import { UserOffice } from "@/type/core/auth/auth.types";
import { Facturation } from "@/type/core/offices/offices.types";
import { OfficeHelper } from "@/utils/helpers/office-helper";
import { Col, Row } from "antd";
import { FunctionComponent } from "react";
import OfficeInfoRow from "@views/core/extranet/menu/account/mySignature/OfficeInfoRow";
import { useTranslation } from "react-i18next";
import { formatPriceWithEuro } from "@/utils/helpers/format-helper";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BalanceTypes } from "@/type/core/qcp/qcp.types";

interface OfficeListProps {
  userOffices: UserOffice[];
  facturation?: Facturation;
  setupAndOpenOrderModal: (office: UserOffice) => void;
  selectedOffice?: UserOffice;
  setSelectedOffice: (selectedOffice: UserOffice) => void;
}

const OfficeList: FunctionComponent<OfficeListProps> = ({
  userOffices,
  facturation,
  setupAndOpenOrderModal,
  selectedOffice,
  setSelectedOffice,
}) => {
  const { t } = useTranslation();

  const getBalanceOfOffice = (id: string) => {
    const office = userOffices.find((userOffice) => userOffice.id === id);
    if (office && office.belongsToGroup && !office.qcpCertificateEnabled) {
      return t("account.mySignature.certificate.qcp.belongsToGroup");
    } else if (
      office &&
      office.belongsToGroup &&
      office.qcpCertificateEnabled
    ) {
      return t("account.mySignature.certificate.qcp.useGroupBalance");
    } else if (office && !!office.withoutBalance) {
      return t("account.mySignature.certificate.qcp.withoutBalance");
    } else if (
      facturation === undefined ||
      facturation.officesBalance.length < 1
    ) {
      return "0€";
    }

    const correctOffice = facturation.officesBalance.filter(
      (officeBalance) => officeBalance.officeId === id,
    );

    if (
      correctOffice.length < 1 ||
      (correctOffice[0].availableCreditInCents &&
        correctOffice[0].availableCreditInCents <= 0)
    ) {
      return "0€";
    } else {
      return formatPriceWithEuro(
        (correctOffice[0]?.availableCreditInCents ?? 0) / 100,
      );
    }
  };

  const correctTranslation = (
    hasEnoughBalance: BalanceTypes,
    officeId: string,
    officeAlias: string,
  ): string => {
    switch (hasEnoughBalance) {
      case "groupDisabled":
      case "belongsToGroup":
      case "withoutBalanceDisabled":
        return `${officeAlias} : ${t(`account.mySignature.certificate.qcp.${hasEnoughBalance}`)}`;
      case "enoughBalance":
        return `${officeAlias} : ${getBalanceOfOffice(officeId)}`;
      case "notEnoughBalance":
        return `${officeAlias} : ${getBalanceOfOffice(officeId)} (${t("account.mySignature.certificate.qcp.notEnoughBalance")})`;
    }
  };

  return (
    <Row className={"pt-3"}>
      <Col>
        {userOffices.map((office) => {
          const officeId = office.id;
          const hasEnoughBalance: BalanceTypes =
            OfficeHelper.officeHasEnoughBalance(office, facturation);
          const officeAlias = office.alias;
          return (
            <div key={officeId}>
              <label className="ml-0 mb-2 d-flex flex-row">
                <div className="">
                  {hasEnoughBalance === "enoughBalance" ? (
                    <input
                      className="ml-0 mr-8 accent-primary"
                      type="radio"
                      value={officeId}
                      checked={selectedOffice?.id === officeId}
                      onChange={() => setSelectedOffice(office)}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="text-danger mr-4"
                    />
                  )}
                </div>
                {correctTranslation(hasEnoughBalance, officeId, officeAlias)}
                {hasEnoughBalance !== "enoughBalance" && (
                  <OfficeInfoRow
                    office={office}
                    setupAndOpenOrderModal={setupAndOpenOrderModal}
                  />
                )}
              </label>
            </div>
          );
        })}
      </Col>
    </Row>
  );
};

export default OfficeList;
